import { format } from 'date-fns';
import React, { useState } from 'react';

import { BasicModal } from '@DesignSystem/modals';
import { ActionMenuList, List, ListItem } from '@ds';
import { DSDeleteIcon, DSEditIcon, DSOptionsIcon } from '@ds-icons';
import { usePanelLibraryTemplateRevisions } from '@mpm-druid/api';
import {
  LibraryPanelTemplate,
  LibraryPanelTemplateRevisionSummary,
  UnsavedLibraryPanelTemplate
} from '@mpm-druid/types';

import styles from './PythonTemplateSelectOption.module.scss';

type PythonTemplateSelectOptionProps = {
  data: UnsavedLibraryPanelTemplate | LibraryPanelTemplate;
  onClick: (revisionId?: string) => void;
  onEditTemplate: () => void;
  onDeleteTemplate: () => void;
  selectedRevisionId?: string;
  selectedTemplateId: string | null;
};

export function PythonTemplateSelectOption({
  data,
  onClick,
  onEditTemplate,
  onDeleteTemplate,
  selectedRevisionId,
  selectedTemplateId
}: PythonTemplateSelectOptionProps) {
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(
    false
  );
  const isSelected = selectedTemplateId === data.id;
  const { data: revisions } = usePanelLibraryTemplateRevisions(
    { templateId: data.id ?? '' },
    // TODO: Only enable if the submenu is open?
    { enabled: true }
  );

  const handleItemClick = () => {
    onClick();
  };

  const handleDeleteTemplate = () => {
    setIsDeleteConfirmationOpen(true);
  };

  const renderRevisionItem = (
    revisionSummary: LibraryPanelTemplateRevisionSummary
  ) => {
    return (
      <ListItem
        active={
          selectedRevisionId
            ? revisionSummary.id === selectedRevisionId
            : revisionSummary.id === revisions?.[0].id
        }
        onClick={() => onClick(revisionSummary.id)}
        key={revisionSummary.id}
        dataTest="version-history-item"
      >
        <ListItem.Text
          primary={format(new Date(revisionSummary.created_at), 'PP pp')}
          secondary={revisionSummary.created_by}
        />
      </ListItem>
    );
  };

  return (
    <>
      <div className={styles.optionContainer}>
        <ListItem
          active={isSelected}
          onClick={handleItemClick}
          data-test="template-select-option-item"
        >
          <ListItem.Text primary={data.name} />
          {data.id ? (
            <ListItem.ActionContainer>
              <ActionMenuList className={styles.optionSettingsWrapper}>
                <ActionMenuList.Trigger
                  size="small"
                  data-test="template-select-action-list-trigger"
                />
                <ActionMenuList.Body
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                  paperClassName={styles.optionSettingsContainer}
                >
                  <ListItem
                    dataTest="template-select-action-list-edit"
                    onClick={onEditTemplate}
                  >
                    <ListItem.PrefixContainer>
                      <ListItem.Icon Icon={<DSEditIcon />} />
                    </ListItem.PrefixContainer>
                    <ListItem.Text primary="Edit" />
                  </ListItem>

                  <ListItem
                    dataTest="template-select-action-list-delete"
                    onClick={handleDeleteTemplate}
                  >
                    <ListItem.PrefixContainer>
                      <ListItem.Icon Icon={<DSDeleteIcon />} />
                    </ListItem.PrefixContainer>
                    <ListItem.Text primary="Delete" />
                  </ListItem>
                  <ListItem dataTest="template-select-action-list-version-history">
                    <ListItem.PrefixContainer>
                      <ListItem.Icon Icon={<DSOptionsIcon />} />
                    </ListItem.PrefixContainer>
                    <ListItem.Text primary="Version history" />
                    <ListItem.Submenu>
                      <List>
                        <div className={styles.versionHistoryContainer}>
                          {revisions?.map(renderRevisionItem)}
                        </div>
                      </List>
                    </ListItem.Submenu>
                  </ListItem>
                </ActionMenuList.Body>
              </ActionMenuList>
            </ListItem.ActionContainer>
          ) : null}
        </ListItem>
      </div>
      {isDeleteConfirmationOpen ? (
        <BasicModal
          content={
            <div>
              Removing the template &quot;{data.name}&quot; will affect all
              panels using it.
            </div>
          }
          onClose={() => setIsDeleteConfirmationOpen(false)}
          onPrimaryButtonClick={() => {
            setIsDeleteConfirmationOpen(false);
            onDeleteTemplate();
          }}
          onSecondaryButtonClick={() => setIsDeleteConfirmationOpen(false)}
          open
          primaryButtonText="Delete"
          secondaryButtonText="Cancel"
          title={`Are you sure you want to delete this template?`}
        />
      ) : null}
    </>
  );
}
